import React, { useState, useRef, useEffect } from 'react';

function InteractiveTerminal({ onLocateProject, onOpenWindow }) {
  const [input, setInput] = useState('');
  const [history, setHistory] = useState([
    { text: 'Welcome to Jeremy Roy Portfolio OS (v1.2.0)', type: 'system' },
    { text: "Type 'help' to see the list of available commands.", type: 'system' },
    { text: '', type: 'empty' }
  ]);
  const terminalEndRef = useRef(null);
  const inputRef = useRef(null);

  const focusInput = () => {
    if (inputRef.current) inputRef.current.focus();
  };

  useEffect(() => {
    focusInput();
  }, []);

  useEffect(() => {
    if (terminalEndRef.current) {
      terminalEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [history]);

  const handleCommand = (cmdStr) => {
    const trimmed = cmdStr.trim();
    if (!trimmed) {
      setHistory(prev => [...prev, { text: '➜  jeremy-roy git:(main) ✗ ', type: 'prompt' }]);
      return;
    }

    const args = trimmed.split(/\s+/);
    const command = args[0].toLowerCase();
    const target = args.slice(1).join(' ').toLowerCase();

    let output = [];

    switch (command) {
      case 'help':
        output = [
          { text: 'Available commands:', type: 'output' },
          { text: '  help              Show this guide', type: 'output' },
          { text: '  ls                List mock directories & files', type: 'output' },
          { text: '  cat [file]        Read file contents (e.g., cat about_me.txt)', type: 'output' },
          { text: '  neofetch          Display system stats & profile overview', type: 'output' },
          { text: '  skills            List engineer competencies', type: 'output' },
          { text: '  contact           Show email & social credentials', type: 'output' },
          { text: '  locate [project]  Center GIS map on project (water, geospatial, environmental)', type: 'output' },
          { text: '  gui [window]      Launch graphical window (about, projects, contact)', type: 'output' },
          { text: '  clear             Clear terminal screen', type: 'output' }
        ];
        break;
      case 'ls':
        output = [
          { text: 'about_me.txt    skills.md    projects.json    contact_info.sh', type: 'output' }
        ];
        break;
      case 'clear':
        setHistory([]);
        setInput('');
        return;
      case 'neofetch':
        output = [
          { text: 'neofetch', type: 'neofetch' }
        ];
        break;
      case 'skills':
        output = [
          { text: '➜ Jeremy Roy Skills profile:', type: 'output' },
          { text: '  • Data Engineering: Data pipelines, ETL workflows, spatial data engineering', type: 'output' },
          { text: '  • GIS Systems: WebGIS client design, geospatial risk indexing, Leaflet, Mapbox', type: 'output' },
          { text: '  • Software Stack: Python (Pandas/NumPy), SQL, Node.js, React, Git, PostGIS', type: 'output' },
          { text: '  • Mechanical Engineering: Hydraulics, telemetry sensing, systems instrumentation', type: 'output' }
        ];
        break;
      case 'contact':
        output = [
          { text: '➜ Contact Channels:', type: 'output' },
          { text: '  • Email: jeremy@benred.co.za', type: 'output' },
          { text: '  • LinkedIn: https://www.linkedin.com/in/jeremy-roy-52280269/', type: 'output' }
        ];
        break;
      case 'gui':
        if (target === 'about' || target === 'projects' || target === 'contact') {
          onOpenWindow(target);
          output = [{ text: `Launching graphical card for ${target}...`, type: 'success' }];
        } else {
          output = [{ text: "Error: GUI card target not found. Supported: about, projects, contact", type: 'error' }];
        }
        break;
      case 'cat':
        if (!target) {
          output = [{ text: 'Error: Provide file parameter (e.g. cat about_me.txt)', type: 'error' }];
        } else if (target.includes('about')) {
          output = [
            { text: 'Mechanical Engineer by training, turned Data/GIS Software Engineer.', type: 'output' },
            { text: 'Specialist in telemetry systems, data pipelines, and analytics dashboarding.', type: 'output' },
            { text: 'Currently handling smart water utilities systems analytics for a major UK utility.', type: 'output' }
          ];
        } else if (target.includes('skills')) {
          output = [
            { text: '# Professional Engineering competencies:', type: 'output' },
            { text: '- Languages: Python, Spark, Shell, SQL', type: 'output' },
            { text: '- Analytics: Pandas, PostGIS, Advanced Spatial Analytics, telemetry analysis', type: 'output' },
            { text: '- Core Engineering: Hydraulics modeling, system automation, telemetry instrumentation', type: 'output' }
          ];
        } else if (target.includes('contact')) {
          output = [
            { text: 'Email: jeremy@benred.co.za', type: 'output' },
            { text: 'LinkedIn: https://www.linkedin.com/in/jeremy-roy-52280269/', type: 'output' }
          ];
        } else if (target.includes('projects')) {
          output = [
            { text: '[', type: 'output' },
            { text: '  {', type: 'output' },
            { text: '    "id": "water",', type: 'output' },
            { text: '    "name": "Smart Water Utility Visualisation",', type: 'output' },
            { text: '    "desc": "Realtime telemetry ingestion on a Web-GIS interactive grid.",', type: 'output' },
            { text: '    "location": "United Kingdom"', type: 'output' },
            { text: '  },', type: 'output' },
            { text: '  {', type: 'output' },
            { text: '    "id": "geospatial",', type: 'output' },
            { text: '    "name": "Geospatial Risk Analysis Tool",', type: 'output' },
            { text: '    "desc": "GIS analytics engine representing environment risk modeling.",', type: 'output' },
            { text: '    "location": "Cape Town, SA"', type: 'output' },
            { text: '  }', type: 'output' },
            { text: ']', type: 'output' }
          ];
        } else {
          output = [{ text: `cat: ${args[1]}: File does not exist`, type: 'error' }];
        }
        break;
      case 'locate':
        if (!target) {
          output = [
            { text: 'Error: Provide project name (locate water | locate geospatial | locate environmental)', type: 'error' }
          ];
        } else if (target === 'water' || target.includes('water')) {
          onLocateProject('water');
          output = [{ text: 'Locating Smart Water Telemetry on Map (Fly to United Kingdom)...', type: 'success' }];
        } else if (target === 'geospatial' || target.includes('geo')) {
          onLocateProject('geospatial');
          output = [{ text: 'Locating Geospatial Analysis Tool on Map (Fly to Cape Town)...', type: 'success' }];
        } else if (target === 'environmental' || target.includes('environ')) {
          onLocateProject('environmental');
          output = [{ text: 'Locating Environmental Impact dashboard on Map (Fly to Johannesburg)...', type: 'success' }];
        } else {
          output = [{ text: `locate: Project "${target}" not identified.`, type: 'error' }];
        }
        break;
      default:
        output = [
          { text: `sh: command not recognized: ${command}. Try 'help'.`, type: 'error' }
        ];
    }

    setHistory(prev => [
      ...prev,
      { text: `➜  jeremy-roy git:(main) ✗ ${cmdStr}`, type: 'prompt' },
      ...output,
      { text: '', type: 'empty' }
    ]);
    setInput('');
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleCommand(input);
    }
  };

  return (
    <div className="terminal-screen" onClick={focusInput}>
      <div className="terminal-history">
        {history.map((line, idx) => {
          if (line.type === 'neofetch') {
            return <Neofetch key={idx} />;
          }
          return (
            <div
              key={idx}
              className="terminal-line"
              style={{
                color: line.type === 'error' ? '#ef4444' :
                  line.type === 'success' ? '#22c55e' :
                    line.type === 'prompt' ? '#38bdf8' :
                      line.type === 'system' ? '#94a3b8' : 'inherit'
              }}
            >
              {line.text}
            </div>
          );
        })}
        <div ref={terminalEndRef} />
      </div>
      <div className="terminal-input-row">
        <span className="terminal-prompt-label" style={{ color: '#38bdf8' }}>➜  jeremy-roy git:(main) ✗ </span>
        <input
          ref={inputRef}
          type="text"
          className="terminal-input"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="off"
          spellCheck="false"
        />
      </div>
    </div>
  );
}

function Neofetch() {
  return (
    <div className="neofetch-container" style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap', padding: '0.5rem 0' }}>
      <pre className="neofetch-logo" style={{ color: '#38bdf8', fontFamily: 'monospace', fontSize: '0.8rem', lineHeight: '1.1' }}>
        {`   _                                  
  | | ___ _ __ ___ _ __ ___  _   _    
  | |/ _ \\ '__/ _ \\ '_ \` _ \\| | | |   
  | |  __/ | |  __/ | | | | | |_| |   
  |_|\\___|_|  \\___|_| |_| |_|\\__, |   
                             |___/    `}
      </pre>
      <div className="neofetch-info" style={{ display: 'flex', flexDirection: 'column', gap: '2px', fontSize: '0.82rem' }}>
        <div className="neofetch-title" style={{ color: '#22c55e', fontWeight: 'bold' }}>jeremy@portfolio</div>
        <div style={{ borderBottom: '1px solid #1e293b' }}></div>
        <div><span style={{ color: '#38bdf8' }}>OS</span>: JeremyRoy PortfolioOS v1.2</div>
        <div><span style={{ color: '#38bdf8' }}>Host</span>: jeremyroy.github.io</div>
        <div><span style={{ color: '#38bdf8' }}>Engine</span>: React 19.1 & Leaflet GIS</div>
        <div><span style={{ color: '#38bdf8' }}>Shell</span>: Mock Zsh Command Terminal</div>
        <div><span style={{ color: '#38bdf8' }}>Profession</span>: Mechanical / Data Engineer</div>
        <div><span style={{ color: '#38bdf8' }}>Specialty</span>: Smart Water Ingestion Pipelines</div>
      </div>
    </div>
  );
}

export default InteractiveTerminal;
